import React, { useEffect, useRef, useState } from "react";
import { Heart, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PUB_ID = process.env.REACT_APP_ADSENSE_PUB_ID;
const SLOT_ID = process.env.REACT_APP_ADSENSE_SLOT_ID;
const KOFI_URL = process.env.REACT_APP_KOFI_URL;

// Sticky bottom banner. Renders a Google AdSense unit when a Publisher ID is
// configured, otherwise falls back to a house ad promoting the monthly OSRS Bond.
// Supporters get an ad-free experience — the banner is hidden entirely.
export default function AdBanner() {
  const { user } = useAuth();
  const isSupporter = !!user?.is_supporter;
  const pushed = useRef(false);
  const bannerRef = useRef(null);
  const [pot, setPot] = useState(null);

  // Collapse the reserved banner height when hidden so the chat FAB drops down.
  useEffect(() => {
    if (isSupporter) document.documentElement.style.setProperty("--ad-banner-h", "0px");
  }, [isSupporter]);

  // Publish the banner's real height so the chat FAB / install prompt can sit above it
  // regardless of how the content wraps on small screens.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--ad-banner-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--ad-banner-h", "0px");
    };
  }, []);

  useEffect(() => {
    if (PUB_ID) return;
    api.get("/donations/pot?scope=season").then(({ data }) => setPot(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!PUB_ID) return;
    // Load the AdSense script once.
    if (!document.querySelector('script[data-adsense="1"]')) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUB_ID}`;
      s.crossOrigin = "anonymous";
      s.setAttribute("data-adsense", "1");
      document.head.appendChild(s);
    }
    if (!pushed.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (e) {
        /* adsbygoogle not ready yet */
      }
    }
  }, []);

  const goal = pot?.goal || 9.99;
  const raised = pot?.raised || 0;
  const pct = Math.min(100, Math.round((raised / goal) * 100));

  if (isSupporter) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-40 osrs-bar border-t-4 border-t-[#0b0906] shadow-[inset_0_3px_0_#7d6a44] px-3 py-2 flex items-center justify-center min-h-[64px]"
      data-testid="ad-banner"
    >
      {PUB_ID ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%", height: 60 }}
          data-ad-client={PUB_ID}
          data-ad-slot={SLOT_ID}
          data-ad-format="horizontal"
          data-full-width-responsive="true"
          data-testid="adsense-unit"
        />
      ) : (
        <div className="flex items-center justify-center gap-4 flex-wrap w-full max-w-4xl" data-testid="house-ad">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("taskscape:open-bondpot"))}
            data-testid="go-ad-free-btn"
            className="osrs-btn px-3 py-1.5 text-xs flex items-center gap-1.5 shrink-0 order-first sm:order-none"
            style={{ background: "linear-gradient(#5a4a10,#3a2f0a)", color: "#FFD700" }}
            title="Become a Supporter — remove ads & get 5 free skips a month"
          >
            <Crown size={14} className="fill-[#FFD700] text-[#FFD700]" /> Go ad-free · 5 skips/mo
          </button>
          <Heart size={22} className="shrink-0 text-[#ff6b6b]" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} data-testid="bond-emblem" />
          <div className="flex-1 min-w-[220px] max-w-lg">
            {pot?.sponsors?.length ? (
              <div className="flex items-center gap-1.5" data-testid="banner-top-supporter">
                <Crown size={14} className="fill-[#FFD700] text-[#FFD700] shrink-0" />
                <span className="osrs-shadow text-[#FFCF3F] text-xs sm:text-sm font-bold truncate">
                  Top supporter: <span className="text-[#FFD700]">{pot.sponsors[0].name}</span>
                  <span className="osrs-num text-[#00FF00]"> ${pot.sponsors[0].amount?.toFixed(2)}</span> — legend! 🙌
                </span>
              </div>
            ) : (
              <span className="osrs-shadow text-[#FFCF3F] text-xs sm:text-sm font-bold" data-testid="banner-top-supporter">
                Support TaskScape — be this month's top supporter! 👑
              </span>
            )}
            <span className="osrs-shadow text-[#7CFC7C] text-[10px] hidden sm:block mt-1" data-testid="banner-guarantee">
              💚 Donations keep the servers running & get your name on the board. 100% optional — never pay to play or win.
            </span>
          </div>
          {KOFI_URL && (
            <a
              href={KOFI_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="banner-kofi-btn"
              className="osrs-btn osrs-btn-green px-4 py-2 text-sm flex items-center gap-2 shrink-0"
            >
              <Heart size={16} /> Chip in
            </a>
          )}
        </div>
      )}
    </div>
  );
}
